<template>
  <div class="profile-page__container">
    <div class="profile__container">
      <UserDataPlaceholder
        v-if="status === 'pending'"
        class="profile__sidebar"
      />
      <!-- v-if="user.state === 'fulfilled' && isSidebarShow" -->
      <div
        v-else
        :class="sidebarClass"
        class="profile__sidebar"
      >
        <UserData
          v-if="!isEditMode && user"
          :avatar="user.avatar"
          :created-date="user.createdDate"
          :description="user.description"
          :full-name="user.fullName"
          :is-own-profile="isOwnProfile"
          :social-links="user.socialLinks"
          :user-id="user.integer_id"
        />
        <button
          v-else
          class="profile__resume-button"
          @click="backToProfile()"
        >
          Назад к профилю
        </button>
      </div>

      <div class="profile__content">
        <div
          v-if="!isEditMode"
          class="profile__tabs"
        >
          <ArrowMenu
            :items="tabs"
            :route-path="routePath"
            theme="profile"
          />
        </div>

        <!-- keep-alive -->
        <KeepAlive>
          <NuxtPage
            :author-name="user ? user.fullName : ''"
            :event-category="eventCategory"
            :is-author="user?.isAuthor"
            :is-own-profile="isOwnProfile"
            :user-id="userIdFromRoute"
          />
        </KeepAlive>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import UserData from '@/components/UserData/UserData.vue';
  import ArrowMenu from '@/components/lh-ui/ArrowMenu/ArrowMenu.vue';
  import { getProfileCreatedDate } from '@devhacker/shared/utils/date';
  import { fetchPublicAuthor } from '@devhacker/shared/features/profile/api';
  import type { RouteRecordNameGeneric } from 'vue-router';
  import { HOME_URL } from '~/constants/config';
  import { useIndexStore } from '~/store';
  import { getErrorCodeForFetch } from '~/utils/errors';
  import { getQueryValue, isPublicProfilePostsPage } from '~/utils/router';
  import UserDataPlaceholder from '~/placeholders/UserDataPlaceholder/UserDataPlaceholder.vue';
  import { ManualSeoInputSchema } from '~/domains/head/seo/api/transformSeo';
  import { parseSeoOrThrow } from '~/domains/head/seo/services/parseSeoOrThrow';
  import { useSeoHead } from '~/domains/head/seo/services/useSeoHead';

  const DEFAULT_AVATAR =
    'data:image/jpeg;base64,R0lGODlhMgAyAMQAAPP1+dzh7eXp8tHY58jR4s3V5O7x9vn6/Oru9NTa6Pz9/uns8/b4+vX2+v7+//b3+trg7MvT5MfQ4vj5+9fd6t7k7uPn8MbO4czT5PHz+NXc6dbc6dLZ6MbP4f///8TN4CH5BAAAAAAALAAAAAAyADIAAAX/YCeOZGmeaKqubJt+cCzPdG3feK7vfF8PFcMk4ivaCIGMZ+kJGJ8xDYBJHUCNmAd164FcexWuGPHVLcToR/kmQLvV61nETffGY206unGPKfViDn0wDIBogx8HhmIdg4qLWxeDE5BbjX1alUyIU5pLiH+aCohnnhODBY+eG32lnh5wcRCvSwt9A7RNfRK5HIO5RH2znraIrwSIH6GLgskBmgbJHxeaCdIfBpCj1x+QVtzLdNwwnYDjiYuX15AY3LyLGtcEmvHJGpoC0nmQfMkKnsHidLCQC0FAKBgQ5KKSwZeRDeUWUjngZIcEC/8k0lnQzsaAbBoXNaAwgwKlkKIqA3wIAQA7';

  const lastRouteName = ref<RouteRecordNameGeneric>('');
  const route = useRoute();
  const router = useRouter();

  const userIdFromRoute = computed(() => getQueryValue(route.params.userId));

  const sidebarClass = computed(() => `profile__sidebar--${route.name?.toString()}`);
  const isEditMode = computed(() => {
    const tabsRouteNameExclude = ['profile-edit', 'profile-settings', 'profile-favorites-edit'];
    return tabsRouteNameExclude.includes(route.name?.toString() ?? '');
  });

  const indexStore = useIndexStore();
  const routePath = computed(() => route.fullPath);
  const isOwnProfile = computed(
    () => indexStore.user?.integer_id?.toString() === userIdFromRoute.value,
  );

  const eventCategory = computed(() => {
    if (isOwnProfile.value) {
      return 'Личный кабинет';
    }

    if (user.value?.isAuthor) {
      return 'Публичный профиль автора';
    }
    return 'Публичный профиль пользователя';
  });

  definePageMeta({
    layout: 'profile',
  });

  const {
    data: user,
    error,
    status,
  } = await useAsyncData(async () => {
    const userId = userIdFromRoute.value;
    if (!userId) {
      throw new Error('No user id in profile.vue');
    }

    const { integer_id, display_name, about, created_at, avatars, is_wp_user, links } =
      await fetchPublicAuthor(userId);

    const mediumAvatar = avatars?.find((item) => item.size === 'm');
    const avatar = mediumAvatar?.url || DEFAULT_AVATAR;

    const findLink = (type: string): string | null =>
      (links || []).find((item: any) => item.type === type)?.url || null;

    const socialLinks = [
      {
        id: 'vk',
        name: 'vkontakte',
        link: findLink('vkontakte'),
      },
      {
        id: 'tg',
        name: 'telegram',
        link: findLink('telegram'),
      },
      {
        id: 'tw',
        name: 'twitter',
        link: findLink('twitter'),
      },
    ];

    const createdDate = created_at
      ? `На <adress>Лайфхакере</adress> <time datetime="${created_at}">c ${getProfileCreatedDate(created_at)} г.</time>`
      : '';

    return {
      avatar,
      fullName: display_name || '',
      description: about || '',
      createdDate,
      isAuthor: Boolean(is_wp_user),
      socialLinks: socialLinks || [],
      integer_id,
    };
  });

  if (error.value) {
    throw createError({ statusCode: getErrorCodeForFetch(error.value) });
  }

  // SEO
  const seo = computed(() => {
    const { fullName = '', description = '', avatar = '' } = user.value || {};
    const isPublic = isPublicProfilePostsPage(route.name);
    const canonical = `${HOME_URL}/profile/user/${userIdFromRoute.value}/posts/`;

    return parseSeoOrThrow(ManualSeoInputSchema, {
      title: `${fullName || 'Профиль'} — Лайфхакер`,
      description: description || `${fullName || 'Профиль пользователя'} на Лайфхакере`,
      canonical,
      robots: isPublic ? 'index,follow' : 'noindex,nofollow',
      image: avatar,
      schema: isPublic
        ? JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Person',
            name: fullName || 'unknown',
            url: canonical,
            image: avatar || 'unknown',
            jobTitle: 'Автор',
            worksFor: {
              '@type': 'Organization',
              name: 'Лайфхакер',
            },
          })
        : '',
    });
  });

  useSeoHead(seo);

  // SEO END

  watch(
    () => route.name,
    (_, oldValue) => {
      lastRouteName.value = oldValue;
    },
  );

  const backToProfile = () => {
    if (lastRouteName.value) {
      const { href } = router.resolve({
        name: lastRouteName.value,
        params: route.params,
        query: route.query,
      });
      location.href = href;
    } else {
      const { href } = router.resolve({
        name: 'profile-comments',
        params: route.params,
        query: route.query,
      });
      location.href = href;
    }
  };

  const tabs = computed(() => {
    const tabs = [
      {
        type: 'posts',
        title: 'Статьи',
        isPublic: true,
        isShow: () => {
          return user.value?.isAuthor;
        },
      },
      {
        type: 'notifications',
        title: 'Уведомления',
        isPublic: false,
      },
      {
        type: 'comments',
        title: 'Комментарии',
        isPublic: true,
      },
      {
        type: 'favorites',
        title: 'Избранное',
        isPublic: false,
      },
    ];
    return tabs
      .filter((tab) => (tab.isShow ? tab.isShow() : tab.isPublic || isOwnProfile.value))
      .map((tab, index) => {
        return {
          backgroundColor: '',
          id: index,
          isActive: route.name === `profile-${tab.type}`,
          isInternalUrl: true,
          link: `/profile/user/${userIdFromRoute.value}/${tab.type}/`,
          testPixel: '',
          textColor: '',
          title: tab.title,
        };
      });
  });
</script>

<style lang="scss" scoped>
  $background: white;

  :deep(.profile-content) {
    background-color: white;
    border: 1px solid #e7e7e7;
    height: 100%;
    padding-bottom: 40px;
  }

  .profile {
    &__container {
      @include margin-x(auto);
      display: flex;
      flex-direction: column;
      width: 100%;
      max-width: 640px;
      flex-grow: 1;

      @include tablet {
        margin-top: 24px;
      }

      @include big-tablet {
        @include margin-x(auto);
        max-width: 964px;
        flex-direction: row-reverse;
        justify-content: space-between;
      }
    }

    &__header {
      display: none;
    }

    &__sidebar {
      background-color: #fff;
      border: 1px solid $borderColor;
      margin-bottom: 16px;

      @include tablet {
        margin-bottom: 24px;
      }

      @include big-tablet {
        position: sticky;
        top: 72px;
        width: 300px;
        min-width: 300px;
        height: fit-content;
      }
    }

    &__tabs {
      display: flex;
      min-height: 48px;
      padding: 0 16px;
      border-top: 1px solid $borderColor;
      border-left: 1px solid $borderColor;
      border-right: 1px solid $borderColor;
      background-color: #fff;

      @include tablet {
        padding: 0 24px;
      }
    }

    &__content {
      min-height: 320px;
      // background-color: white;
      // border: 1px solid #E7E7E7;

      @include tablet {
        min-height: 464px;
      }

      @include big-tablet {
        max-width: 640px;
        margin-right: 24px;
      }
    }

    &__resume-button {
      display: none;

      @include big-tablet {
        border: none;
        background: none;
        user-select: none;
        @include fontTextBtn;
        color: #2962f9;
        padding: 16px;
        position: relative;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        cursor: pointer;
        margin: 15px 0;

        &:before {
          content: '';
          display: block;
          width: 24px;
          height: 24px;
          margin-right: 8px;
          @include arrowMask;
          background-color: #2962f9;
        }

        @include hover {
          color: #264eb1;

          &:before {
            background-color: #264eb1;
          }
        }
      }
    }
  }

  .profile-page__container {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }
</style>
